import { useState, useRef, useCallback } from 'react'
import { useMediaQuery } from '@/features/shared/hooks/useMediaQuery'
import { PopoverMaxWidth } from '@/features/shop/components/ProductFilter/FilterPopover'
import { useFiltersContext } from '@/features/shop/components/ProductFilter/FiltersContext'
import { SearchType } from '@/features/shop/services/Search/types'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicBuyAgainFilterModal } from '@/features/shop/components/Modal/MobileFilterModals/BuyAgainFilterModal/DynamicBuyAgainFilterModal'
import { DynamicUniversalSearchFilterModal } from '@/features/shop/components/Modal/MobileFilterModals/UniversalSearchFilterModal/DynamicUniversalSearchFilterModal'
import { DynamicSearchFilterModal } from '@/features/shop/components/Modal/SearchFilterModal/DynamicSearchFilterModal'

const mobileFilterModals = {
  [SearchType.SEARCH]: DynamicSearchFilterModal,
  [SearchType.BUY_AGAIN]: DynamicBuyAgainFilterModal,
  [SearchType.UNIVERSAL]: DynamicUniversalSearchFilterModal,
}

export const useOnFilterPillClick = (filter: string) => {
  const { openDialog } = useHandleDialog()
  const { screenSm } = useMediaQuery()
  const { searchType } = useFiltersContext()

  const [willOverflow, setWillOverflow] = useState(false)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const togglePopover = () => setIsPopoverOpen(!isPopoverOpen)

  const ref = useRef<HTMLDivElement | null>(null)

  const getRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return
    ref.current = node

    const calculateWillOverflow = () => {
      const { right } = node.getBoundingClientRect()
      // This is to check if the filter popover on the right half side of the
      // viewport will potentially overflow or not when it is opened.
      const windowWidth = document.documentElement.clientWidth + 72 // include container padding
      const calculatedWillOverFlow =
        right > windowWidth / 2 && right + PopoverMaxWidth > windowWidth
      setWillOverflow(calculatedWillOverFlow)
    }
    calculateWillOverflow()

    window.addEventListener('resize', calculateWillOverflow)
    return () => {
      window.removeEventListener('resize', calculateWillOverflow)
    }
  }, [])

  if (screenSm && isPopoverOpen) {
    setIsPopoverOpen(false)
  }

  const onFilterPillClick = () => {
    if (screenSm) {
      const modal = mobileFilterModals[searchType]
      openDialog(modal, { filterToAutoExpand: filter })
    } else {
      togglePopover()
    }
  }

  return {
    ref,
    getRef,
    willOverflow,
    isPopoverOpen,
    togglePopover,
    onFilterPillClick,
  }
}
